import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode, KeyRound, LogOut, RefreshCw, Smartphone, CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { inputCls } from "./shared";

const STATE = {
  connected: ["Terhubung", "bg-emerald-600 text-white"], qr: ["Menunggu scan QR", "bg-amber-100 text-amber-900"],
  pairing: ["Menunggu kode pairing", "bg-amber-100 text-amber-900"], starting: ["Menyiapkan...", "bg-slate-100 text-slate-700"],
  reconnecting: ["Menyambung ulang...", "bg-amber-100 text-amber-900"], idle: ["Belum terhubung", "bg-slate-200 text-slate-700"],
  logged_out: ["Terputus", "bg-rose-600 text-white"], error: ["Error", "bg-rose-600 text-white"], offline: ["Engine memulai", "bg-slate-200 text-slate-700"],
};

function Connected({ st, onLogout, busy }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="wa-connected-box">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="text-emerald-600" size={28} />
        <div><p className="text-sm font-bold text-[#0F2B48]" data-testid="wa-device-name">{st.me?.name || "WhatsApp"}</p>
          <p className="font-mono text-xs text-slate-600" data-testid="wa-device-phone">+{st.me?.phone}</p></div>
      </div>
      <button onClick={onLogout} disabled={busy} className="flex items-center gap-1.5 rounded-lg border border-rose-300 px-3 py-2 text-xs font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-50" data-testid="wa-logout-button"><LogOut size={13} /> Putuskan</button>
    </div>
  );
}

function Linker({ st, busy, onConnect }) {
  const [mode, setMode] = useState("qr");
  const [phone, setPhone] = useState("");
  const tab = (m, Icon, l) => <button onClick={() => setMode(m)} className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-bold transition-colors ${mode === m ? "bg-[#0F2B48] text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`wa-mode-${m}`}><Icon size={14} /> {l}</button>;
  return (
    <div>
      <div className="flex gap-1 rounded-xl border p-1">{tab("qr", QrCode, "Scan QR")}{tab("pair", KeyRound, "Kode Pairing (No. WA)")}</div>
      {mode === "qr" ? (
        <div className="mt-4 flex flex-col items-center gap-3">
          {st.state === "qr" && st.qr ? <img src={st.qr} alt="QR WhatsApp" className="h-56 w-56 rounded-lg border bg-white p-1" data-testid="wa-qr-image" />
            : <div className="flex h-56 w-56 items-center justify-center rounded-lg border border-dashed text-slate-400">{busy || st.state === "starting" ? <Loader2 className="animate-spin" /> : <QrCode size={48} />}</div>}
          <button onClick={() => onConnect({ mode: "qr" })} disabled={busy} className="flex items-center gap-1.5 rounded-lg bg-[#D97706] px-4 py-2 text-xs font-bold text-white hover:bg-[#B45309] disabled:opacity-50" data-testid="wa-generate-qr-button"><RefreshCw size={13} /> {st.state === "qr" ? "Muat Ulang QR" : "Tampilkan QR"}</button>
          <p className="text-center text-[11px] text-slate-500">WhatsApp di ponsel → <b>Perangkat Tertaut</b> → <b>Tautkan Perangkat</b> → scan QR. QR berganti otomatis ±20 detik.</p>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="flex gap-2">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="08xxxxxxxxxx / 628xxxxxxxxxx" className={inputCls} data-testid="wa-pair-phone-input" />
            <button onClick={() => onConnect({ mode: "pair", phone })} disabled={busy || phone.replace(/\D/g, "").length < 9} className="shrink-0 rounded-lg bg-[#D97706] px-4 text-xs font-bold text-white hover:bg-[#B45309] disabled:opacity-50" data-testid="wa-get-pair-code-button">{busy ? "Meminta..." : "Minta Kode"}</button>
          </div>
          {st.state === "pairing" && st.pair_code && <p className="rounded-xl bg-slate-900 py-4 text-center font-mono text-3xl font-bold tracking-[0.3em] text-white" data-testid="wa-pair-code">{st.pair_code}</p>}
          <p className="text-[11px] text-slate-500">WhatsApp di ponsel → <b>Perangkat Tertaut</b> → <b>Tautkan Perangkat</b> → <b>Tautkan dengan nomor telepon</b> → masukkan kode di atas.</p>
        </div>
      )}
    </div>
  );
}

export function WhatsAppDevice() {
  const [st, setSt] = useState({ state: "offline" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/wa/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);
  const run = async (fn) => { setBusy(true); try { setSt((await fn()).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const connect = (body) => run(() => api.post("/admin/wa/connect", body));
  const logout = () => window.confirm("Putuskan perangkat WhatsApp ini?") && run(() => api.post("/admin/wa/logout"));
  const [label, cls] = STATE[st.state] || [st.state, "bg-slate-100 text-slate-700"];
  return (
    <div data-testid="wa-device-panel">
      <div className="mb-4 flex items-center justify-between">
        <p className="flex items-center gap-2 text-xs font-semibold text-slate-600"><Smartphone size={14} /> Perangkat WhatsApp</p>
        <span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${cls}`} data-testid="wa-device-status">{label}</span>
      </div>
      {st.error && st.state !== "connected" && <p className="mb-3 rounded-lg bg-rose-50 p-2 text-xs text-rose-800" data-testid="wa-device-error">{st.error}</p>}
      {st.state === "connected" ? <Connected st={st} onLogout={logout} busy={busy} /> : <Linker st={st} busy={busy} onConnect={connect} />}
    </div>
  );
}
